import * as React from 'react';
import { ReactElement, ReactNode, useContext, useEffect, useState } from 'react';
import {
  AdminDataProjectDTO,
  ExternalIdDTO,
  ProjectDTO,
  ProjectFundingReferenceDTO,
  ProjectInstitutionalIdDTO,
  ProjectOtherNameDTO,
  ProjectOtherPartnerDTO,
  ProjectPersonOtherDTO,
  ProjectToPersonDTO,
  ReferencedOrganisationDTO,
  ReferencedProjectDTO,
  SubjectDTO,
  UrlDTO
} from '../../../api/core';
import { AuthContext } from '../../../Auth/components/AuthProvider';
import { Layout } from '../../Layout';
import { IntlShape, useIntl } from 'react-intl';
import { FormContent } from '../FormContent';
import { Validation } from '../../../util/validation/interfaces';
import { validate } from '../../../util/validation/validate';
import { CheckPermissionFunction, PermissionGate } from '../../../Auth/components/PermissionGate';
import { OtherNames } from '../components/parts/OtherNames';
import { Urls } from '../components/parts/Urls';
import { ExternalIds } from '../components/parts/ExternalIds';
import { InstitutionalIds } from '../components/parts/InstitutionalIds';
import { Subjects } from '../components/parts/Subjects';
import { ProjectOtherPartners } from '../components/parts/ProjectOtherPartners';
import { ReferencedOrganisations } from '../components/parts/ReferencedOrganisations';
import { ProjectFundingReferences } from '../components/parts/ProjectFundingReferences';
import { ReferencedProjects } from '../components/parts/ReferencedProjects';
import { EditorialStatus } from '../components/parts/EditorialStatus';
import { Block } from '../components/layout/Block';
import { LabelledInputText } from '../../components/form/LabelledInputText';
import { LabelledInputTextarea } from '../../components/form/LabelledInputTextarea';
import { LabelledInputNumber } from '../../components/form/LabelledInputNumber';
import { getVocabularyOptions } from '../../../Vocabulary/util';
import { Hidden } from '../components/parts/Hidden';
import { LabelledCalendar } from '../../components/form/LabelledCalendar';
import { External, Internal, LinkedPerson } from '../components/LinkedPerson';
import { getDefaultLinkedPerson, getDefaultLinkedPersonOther } from '../../../Settings/emptyModels';
import { Panel } from '../components/layout/Panel';

interface Props {
  modifiedModel: ProjectDTO;
  submit: () => void;
  updateField: (field: keyof ProjectDTO, value: any) => void;
  updateAdminField: (field: keyof AdminDataProjectDTO, value: any) => void;
  saving: boolean;
}

export const FormProject: React.FC<Props> = (props: Props): ReactNode => {
  const intl: IntlShape = useIntl();
  const {session} = useContext(AuthContext);

  const validation: Validation = validate(props.modifiedModel, props.modifiedModel.adminDataProject, 'project', session);


  const [initiallyLoaded, setInitiallyLoaded] = React.useState(false);
  const [autoFocusPerson, setAutoFocusPerson] = React.useState(false);

  useEffect(() => {
    setInitiallyLoaded(true);
  }, []);


  const updateField = (field: keyof ProjectDTO, value: any): void => {

    switch (field) {
      case 'projectPersons':
        setAutoFocusPerson(true);
        break;
      case 'projectPersonOthers':
        setAutoFocusPerson(true);
        break;
      default:
        setAutoFocusPerson(false);
    }
    props.updateField(field, value);
  };

  return (
    <Layout
      title={
        props.modifiedModel.id
          ? intl.formatMessage({ id: 'project.header.edit' })
          : intl.formatMessage({ id: 'project.header.create' })
      }
      headerImage={require('../../../Settings/img/banners/edit/project.svg')}
    >
      <FormContent onSave={props.submit} validation={validation} isNew={!props.modifiedModel.id} saving={props.saving}>
        <Block title={'project.header.basic'}>
          <PermissionGate always={false} entity="project" field="name">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <LabelledInputText
                  id={`name`}
                  autoFocus={true}
                  value={props.modifiedModel.name}
                  permissions={checkPermission()}
                  validation={validation?.root?.fields?.name}
                  onChange={(name): void => updateField('name', name)}
                  title={'project.attr.name'}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate entity="project" always={false} field="other_names">
            {(checkPermission: CheckPermissionFunction) => (
              <div className={'col-12'}>
                <OtherNames
                  autoFocus={initiallyLoaded}
                  otherNames={props.modifiedModel.otherNames || []}
                  onChange={(otherNames: ProjectOtherNameDTO[]): void => updateField('otherNames', otherNames)}
                  checkPermission={checkPermission}
                  validation={validation?.root?.fields?.other_names}
                  label={'project.attr.otherNames'}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="project" field="urls">
            {(checkPermission: CheckPermissionFunction) => (
              <div className={'col-12'}>
                <Urls
                  autoFocus={initiallyLoaded}
                  title={'project.attr.urls'}
                  checkPermission={checkPermission}
                  urls={props.modifiedModel.urls || []}
                  typelist={'url_list'}
                  onChange={(u: UrlDTO[]): void => updateField('urls', u)}
                  validation={validation?.root?.fields?.urls}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="project" field="note">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <LabelledInputTextarea
                  id={'note'}
                  value={props.modifiedModel.note}
                  permissions={checkPermission()}
                  validation={validation?.root?.fields?.note}
                  onChange={(note: string): void => updateField('note', note)}
                  autoResize={true}
                  rows={3}
                  title={'project.attr.note'}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="project" field="start_date">
            {(checkPermission: CheckPermissionFunction) => (
              <div className={'col-6'}>
                <LabelledCalendar
                  id={'start_date'}
                  value={props.modifiedModel.startDate}
                  permissions={checkPermission()}
                  validation={validation?.root?.fields?.start_date}
                  onChange={(startDate): void => updateField('startDate', startDate)}
                  title={'project.attr.startDate'}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="project" field="end_date">
            {(checkPermission: CheckPermissionFunction) => (
              <div className={'col-6'}>
                <LabelledCalendar
                  id={'id'}
                  value={props.modifiedModel.endDate}
                  permissions={checkPermission()}
                  validation={validation?.root?.fields?.end_date}
                  onChange={(endDate): void => updateField('endDate', endDate)}
                  title={'project.attr.endDate'}
                />
              </div>
            )}
          </PermissionGate>
        </Block>
        <Block title={'project.header.ids'}>
          <PermissionGate always={false} entity="project" field="external_ids">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <ExternalIds
                  autoFocus={initiallyLoaded}
                  extIds={props.modifiedModel.externalIds || []}
                  onChange={(externalIds: ExternalIdDTO[]): void => updateField('externalIds', externalIds)}
                  options={getVocabularyOptions(intl.locale, 'external_unit_id_list')}
                  validation={validation?.root?.fields?.external_ids}
                  checkPermission={checkPermission}
                  title="project.attr.externalIds"
                  label="project.attr.externalId"
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="project" field="project_institutional_ids">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <InstitutionalIds
                  autoFocus={initiallyLoaded}
                  checkPermission={checkPermission}
                  typesList={'affiliations_list'}
                  institutionalIds={props.modifiedModel.projectInstitutionalIds || []}
                  onChange={(iId: ProjectInstitutionalIdDTO[]): void => updateField('projectInstitutionalIds', iId)}
                  validation={validation?.root?.fields?.project_institutional_ids}
                  title="project.attr.institutionalIds"
                />
              </div>
            )}
          </PermissionGate>
        </Block>
        <Block title={'project.header.administration'}>
          <PermissionGate always={false} entity="project" field="id">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <LabelledInputNumber
                  id={'id'}
                  value={props.modifiedModel.id || undefined}
                  permissions={checkPermission()}
                  validation={validation?.root?.fields?.id}
                  min={0}
                  allowEmpty={true}
                  maxFractionDigits={0}
                  useGrouping={false}
                  onChange={(): void => console.warn('ID cannot be edited')}
                  title={'project.attr.id'}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="project_admin_data" field="hidden">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12 flex'}>
                <Hidden
                  label={'project.attr.hidden'}
                  checked={props.modifiedModel.adminDataProject.hidden}
                  disabled={!checkPermission().canWrite}
                  invalid={!validation?.root?.fields?.id.valid}
                  required={checkPermission().required}
                  onChange={(hidden: boolean): void => props.updateAdminField('hidden', hidden)}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="project_admin_data" field="editorial_status">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <EditorialStatus
                  label={'project.attr.editorialStatus'}
                  editorialStatus={props.modifiedModel.adminDataProject.editorialStatus}
                  onChange={(value): void => props.updateAdminField('editorialStatus', value)}
                  checkPermission={checkPermission}
                  validation={validation?.admin?.fields?.editorial_status}
                />
              </div>
            )}
          </PermissionGate>
        </Block>
        <Block title={'project.header.linking'}>
          <PermissionGate always={false} entity="project" field="project_subjects">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <Subjects
                  autoFocus={initiallyLoaded}
                  title={'project.attr.subjects'}
                  checkPermission={checkPermission}
                  onChange={(subjects: SubjectDTO[]): void => updateField('projectSubjects', subjects)}
                  subjects={props.modifiedModel.projectSubjects || []}
                  validation={validation?.root?.fields?.project_subjects}
                  type={'project'}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="project" field="partner">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <ReferencedOrganisations
                  autoFocus={initiallyLoaded}
                  organisations={props.modifiedModel.partner || []}
                  validation={validation?.root?.fields?.partner}
                  checkPermission={checkPermission}
                  onChange={(partner: ReferencedOrganisationDTO[]): void => updateField('partner', partner)}
                  multiple={true}
                  label="project.attr.partners"
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate entity="project" always={false} field="project_other_partners">
            {(checkPermission: CheckPermissionFunction) => (
              <div className={'col-12'}>
                <ProjectOtherPartners
                  autoFocus={initiallyLoaded}
                  label={'project.attr.otherPartners'}
                  projectOtherPartners={props.modifiedModel.projectOtherPartners || []}
                  onChange={(otherPartners: ProjectOtherPartnerDTO[]): void =>
                    updateField('projectOtherPartners', otherPartners)
                  }
                  checkPermission={checkPermission}
                  validation={validation?.root?.fields?.project_other_partners}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate entity="project" always={false} field="project_funding_references">
            {(checkPermission: CheckPermissionFunction) => (
              <div className={'col-12'}>
                <ProjectFundingReferences
                  autoFocus={initiallyLoaded}
                  label={'project.attr.fundingReferences'}
                  projectFundingReferences={props.modifiedModel.projectFundingReferences || []}
                  onChange={(otherPartners: ProjectFundingReferenceDTO[]): void =>
                    updateField('projectFundingReferences', otherPartners)
                  }
                  checkPermission={checkPermission}
                  validation={validation?.root?.fields?.project_funding_references}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity={'project'} field={'project_persons'}>
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <PermissionGate always={false} entity={'project'} field="project_person_others">
                {(checkPermissionOther: CheckPermissionFunction): ReactElement => (
                  <div className={'col-12'}>
                    <Panel title={'project.attr.persons'} toggleable={true}>
                      <LinkedPerson
                        internal={props.modifiedModel.projectPersons || []}
                        external={props.modifiedModel.projectPersonOthers || []}
                        enabledExternalIds={false}
                        updateInternal={(update: Internal[]): void => updateField('projectPersons', update)}
                        updateExternal={(update: External[]): void => updateField('projectPersonOthers', update)}
                        newInternal={(): ProjectToPersonDTO => getDefaultLinkedPerson()}
                        newExternal={(): ProjectPersonOtherDTO => getDefaultLinkedPersonOther(false)}
                        validationInternal={validation?.root?.fields?.project_persons}
                        validationExternal={validation?.root?.fields?.project_person_others}
                        checkPermissionInternal={checkPermission}
                        checkPermissionExternal={checkPermissionOther}
                        autoFocus={initiallyLoaded && autoFocusPerson}
                        roleOptions={getVocabularyOptions(intl.locale, 'person_role_list', 'project')}
                      />
                    </Panel>
                  </div>
                )}
              </PermissionGate>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="project" field="childs">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <ReferencedProjects
                  autoFocus={initiallyLoaded}
                  label={'project.attr.childs'}
                  projects={props.modifiedModel.childs || []}
                  validation={validation?.root?.fields?.childs}
                  checkPermission={checkPermission}
                  multiple={true}
                  onChange={(partner: ReferencedProjectDTO[]): void => updateField('childs', partner)}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="project" field="parents">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <ReferencedProjects
                  autoFocus={initiallyLoaded}
                  label={'project.attr.parents'}
                  projects={props.modifiedModel.parents || []}
                  validation={validation?.root?.fields?.parents}
                  checkPermission={checkPermission}
                  multiple={false}
                  onChange={(partner: ReferencedProjectDTO[]): void => updateField('parents', partner)}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="project" field="predecessors">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <ReferencedProjects
                  autoFocus={initiallyLoaded}
                  label={'project.attr.predecessors'}
                  projects={props.modifiedModel.predecessors || []}
                  validation={validation?.root?.fields?.predecessors}
                  checkPermission={checkPermission}
                  multiple={true}
                  onChange={(partner: ReferencedProjectDTO[]): void => updateField('predecessors', partner)}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="project" field="successor">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <ReferencedProjects
                  autoFocus={initiallyLoaded}
                  label={'project.attr.successors'}
                  projects={props.modifiedModel.successor || []}
                  validation={validation?.root?.fields?.successor}
                  checkPermission={checkPermission}
                  multiple={true}
                  onChange={(partner: ReferencedProjectDTO[]): void => updateField('successor', partner)}
                />
              </div>
            )}
          </PermissionGate>
        </Block>
      </FormContent>
    </Layout>
  );
};

export default FormProject;
